/** Routes grouped under the Content authoring nav tree. */
export const CONTENT_AUTHORING_NAV_PATHS = [
  '/templates',
  '/authoring/courses',
  '/authoring/media-collection',
] as const;

export function isContentAuthoringNavPath(pathname: string): boolean {
  return CONTENT_AUTHORING_NAV_PATHS.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  );
}

/**
 * Whether the Content authoring drill-down panel is open.
 *
 * On an authoring route the panel is open by default; `panelOverride === false` collapses it
 * without leaving the page. Off-route, `panelOverride === true` opens it when the user expands
 * Content authoring from the main Compliance Education list.
 */
export function isContentAuthoringPanelOpen(
  pathname: string,
  panelOverride: boolean | null,
): boolean {
  const isOnAuthoringRoute = isContentAuthoringNavPath(pathname);
  return (isOnAuthoringRoute && panelOverride !== false) || panelOverride === true;
}

/** Collapse the drill-down without navigating; reset off-route so authoring routes reopen by default. */
export function contentAuthoringPanelOverrideOnClose(
  isOnAuthoringRoute: boolean,
): boolean | null {
  return isOnAuthoringRoute ? false : null;
}

/** Immersive editor routes where the side nav should collapse for workspace room. */
const CONTENT_AUTHORING_EDITOR_PATH_PATTERNS = [
  /^\/templates\/[^/]+\/content(?:\/|$)/,
  /** Future: client course editor */
  /^\/authoring\/courses\/[^/]+\/content(?:\/|$)/,
] as const;

export function isContentAuthoringEditorPath(pathname: string): boolean {
  return CONTENT_AUTHORING_EDITOR_PATH_PATTERNS.some((pattern) => pattern.test(pathname));
}

/**
 * Route that restores the main Compliance Education nav panel when leaving Content authoring.
 * Picks the first accessible non-authoring destination for the current user.
 */
export function getContentAuthoringNavBackPath(flags: {
  canAccessContentManagement: boolean;
  canAccessDashboard: boolean;
}): string {
  if (flags.canAccessContentManagement) {
    return '/manage-content';
  }
  if (flags.canAccessDashboard) {
    return '/review-center';
  }
  return '/';
}
